import * as Yup from 'yup';

import {
  EIV_CHAR_LENGTH,
  EIV_STATUS,
  TENANT_ID,
  TENANT_WITH_BIRTHDATE,
  VALIDATION_CONSTANTS,
  VALID_STATUSES_FOR_NEW_EXPERTS,
} from 'global/constants';
import { NO_SECOND_QUALIFICATION } from 'global/qualifications';

import { YDate, YFutureDate, YPastDate, YPhoneNumber } from 'utils/YupUtils';
import { getTenantId } from 'utils/tenantUtil';

const canExpertBeOwner = (isMainGas, isMainEnergy, statusEnergy, statusGas) => {
  return (
    (VALID_STATUSES_FOR_NEW_EXPERTS.includes(statusEnergy) && isMainEnergy) ||
    (VALID_STATUSES_FOR_NEW_EXPERTS.includes(statusGas) && isMainGas)
  );
};

const isBirthdateRequired = (statusGas, statusEnergy, isNewExpert) => {
  return isNewExpert || statusGas === EIV_STATUS.NEW || statusEnergy === EIV_STATUS.NEW;
};

const isGuestGasExpertWithValidStatus = (isMainGas, statusGas) => {
  return isMainGas === false && VALID_STATUSES_FOR_NEW_EXPERTS.includes(statusGas);
};

const isGuestEnergyExpertWithValidStatus = (isMainEnergy, statusEnergy) => {
  return isMainEnergy === false && VALID_STATUSES_FOR_NEW_EXPERTS.includes(statusEnergy);
};

const isMainEnergyExpertNew = (isMainEnergy, statusEnergy) => {
  return isMainEnergy && statusEnergy === EIV_STATUS.NEW;
};

const isMainGasExpertNew = (isMainGas, statusGas) => {
  return isMainGas && statusGas === EIV_STATUS.NEW;
};

const needsSecondQualificationEnergy = (isMainEnergy, statusEnergy, firstQualificationEnergy) => {
  return (
    isMainEnergy &&
    statusEnergy === EIV_STATUS.NEW &&
    !NO_SECOND_QUALIFICATION.includes(firstQualificationEnergy)
  );
};

const needsSecondQualificationGas = (isMainGas, statusGas, firstQualificationGas) => {
  return (
    isMainGas &&
    statusGas === EIV_STATUS.NEW &&
    !NO_SECOND_QUALIFICATION.includes(firstQualificationGas)
  );
};

export const YExpert = Yup.object().shape({
  id: Yup.number().required(VALIDATION_CONSTANTS.MANDATORY),
  lastName: Yup.string()
    .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
    .max(EIV_CHAR_LENGTH.EXPERT_NAME, VALIDATION_CONSTANTS.TOO_LONG)
    .required(VALIDATION_CONSTANTS.MANDATORY),
  birthdate: Yup.string().when(['statusGas', 'statusEnergy', 'isNewExpert'], {
    is: (statusGas, statusEnergy, isNewExpert) =>
      isBirthdateRequired(statusGas, statusEnergy, isNewExpert),
    then: TENANT_WITH_BIRTHDATE.includes(getTenantId())
      ? YPastDate.required(VALIDATION_CONSTANTS.MANDATORY)
      : Yup.string(),
  }),
  firstName: Yup.string()
    .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
    .max(EIV_CHAR_LENGTH.EXPERT_NAME, VALIDATION_CONSTANTS.TOO_LONG)
    .required(VALIDATION_CONSTANTS.MANDATORY),
  isMainEnergy: Yup.boolean(),
  isMainGas: Yup.boolean(),
  expertGasValidityDate: Yup.string(),
  expertEnergyValidityDate: Yup.string(),
  deletionDateEnergy: YDate,
  deletionDateGas: YDate,
  netzportalEmail: Yup.string().email(VALIDATION_CONSTANTS.INVALID_EMAIL).nullable().optional(),
  newEmail: Yup.string().email(VALIDATION_CONSTANTS.INVALID_EMAIL).optional(),
  confirmEmail: Yup.string()
    .optional()
    .when('newEmail', {
      is: (val) => Boolean(val),
      then: (schema) =>
        schema
          .required('Bitte bestätigen Sie die E-Mail-Adresse')
          .oneOf([Yup.ref('netzportalEmail')], 'Die E-Mail-Adressen stimmen nicht überein'),
    }),
  firstQualificationEnergy: Yup.string().when(['isMainEnergy', 'statusEnergy'], {
    is: (isMainEnergy, statusEnergy) => isMainEnergyExpertNew(isMainEnergy, statusEnergy),
    then: (validation) => validation.required(VALIDATION_CONSTANTS.MANDATORY),
  }),
  firstQualificationGas: Yup.string().when(['isMainGas', 'statusGas'], {
    is: (isMainGas, statusGas) => isMainGasExpertNew(isMainGas, statusGas),
    then: (validation) => validation.required(VALIDATION_CONSTANTS.MANDATORY),
  }),
  telefon: Yup.lazy((value) => {
    if (value !== undefined && value !== '' && getTenantId() === TENANT_ID.MTN) {
      return YPhoneNumber;
    }
    return Yup.string().nullable().optional();
  }),
  secondQualificationEnergy: Yup.string().when(
    ['isMainEnergy', 'statusEnergy', 'firstQualificationEnergy'],
    {
      is: (isMainEnergy, statusEnergy, firstQualificationEnergy) =>
        needsSecondQualificationEnergy(isMainEnergy, statusEnergy, firstQualificationEnergy),
      then: (validation) => validation.required(VALIDATION_CONSTANTS.MANDATORY),
    }
  ),
  secondQualificationGas: Yup.string().when(['isMainGas', 'statusGas', 'firstQualificationGas'], {
    is: (isMainGas, statusGas, firstQualificationGas) =>
      needsSecondQualificationGas(isMainGas, statusGas, firstQualificationGas),
    then: (validation) => validation.required(VALIDATION_CONSTANTS.MANDATORY),
  }),
  isExpertOwner: Yup.bool().when(['isMainGas', 'isMainEnergy', 'statusEnergy', 'statusGas'], {
    is: (isMainGas, isMainEnergy, statusEnergy, statusGas) =>
      canExpertBeOwner(isMainGas, isMainEnergy, statusEnergy, statusGas),
    then: (validation) => validation.required(VALIDATION_CONSTANTS.MANDATORY),
  }),
  isNewExpert: Yup.bool(),
  isExpertEnergyActive: Yup.boolean(),
  isExpertGasActive: Yup.boolean(),
});

export const YExpertPopup = YExpert.shape(
  {
    expertEnergyValidityDate: Yup.string().when(['isMainEnergy', 'statusEnergy'], {
      is: (isMainEnergy, statusEnergy) =>
        isGuestEnergyExpertWithValidStatus(isMainEnergy, statusEnergy),
      then: YFutureDate.required(VALIDATION_CONSTANTS.MANDATORY),
    }),
    expertGasValidityDate: Yup.string().when(['isMainGas', 'statusGas'], {
      is: (isMainGas, statusGas) => isGuestGasExpertWithValidStatus(isMainGas, statusGas),
      then: YFutureDate.required(VALIDATION_CONSTANTS.MANDATORY),
    }),
    isExpertEnergyActive: Yup.boolean().when(['isExpertGasActive'], {
      is: (isExpertGasActive) => !isExpertGasActive,
      then: (validation) =>
        validation.required(VALIDATION_CONSTANTS.MANDATORY).isTrue('Bitte wählen Sie eine Sparte'),
    }),
    isExpertGasActive: Yup.boolean().when(['isExpertEnergyActive'], {
      is: (isExpertEnergyActive) => !isExpertEnergyActive,
      then: (validation) =>
        validation.required(VALIDATION_CONSTANTS.MANDATORY).isTrue('Bitte wählen Sie eine Sparte'),
    }),
  },
  [['isExpertEnergyActive', 'isExpertGasActive']]
);

export const YExpertDelete = Yup.object().shape(
  {
    deleteEnergy: Yup.boolean().when(['deleteGas'], {
      is: (deleteGas) => !deleteGas,
      then: (validation) =>
        validation.required(VALIDATION_CONSTANTS.MANDATORY).isTrue('Bitte wählen Sie eine Sparte'),
    }),
    deletionReasonEnergy: Yup.string().when(['deleteEnergy'], {
      is: (deleteEnergy) => deleteEnergy,
      then: (validation) =>
        validation
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.STANDARD_TEXT, VALIDATION_CONSTANTS.TOO_LONG),
    }),
    deletionDateEnergy: Yup.string().when(['deleteEnergy'], {
      is: (deleteEnergy) => deleteEnergy,
      then: YDate.required(VALIDATION_CONSTANTS.MANDATORY),
    }),

    deleteGas: Yup.boolean().when(['deleteEnergy'], {
      is: (deleteEnergy) => !deleteEnergy,
      then: (validation) =>
        validation.required(VALIDATION_CONSTANTS.MANDATORY).isTrue('Bitte wählen Sie eine Sparte'),
    }),
    deletionReasonGas: Yup.string().when(['deleteGas'], {
      is: (deleteGas) => deleteGas,
      then: (validation) =>
        validation
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.STANDARD_TEXT, VALIDATION_CONSTANTS.TOO_LONG),
    }),
    deletionDateGas: Yup.string().when(['deleteGas'], {
      is: (deleteGas) => deleteGas,
      then: YDate.required(VALIDATION_CONSTANTS.MANDATORY),
    }),
  },
  [['deleteEnergy', 'deleteGas']]
);
